import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const TEMPLATE_COLORS = {
  residential_standard: '#16A34A', residential_premium: '#1E40AF', commercial_basic: '#0F172A',
  commercial_enterprise: '#7C3AED', eco_friendly: '#059669', quick_quote: '#F59E0B',
};
const TEMPLATE_ICONS = {
  residential: 'fa-house', commercial: 'fa-building', specialty: 'fa-star', custom: 'fa-palette',
};

export default function Templates() {
  const [builtIn, setBuiltIn] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'custom', description: '', sections: [], branding: { primary_color: '#16A34A' } });

  const allSections = [
    { id: 'header', label: 'Header' }, { id: 'property_assessment', label: 'Property' },
    { id: 'system_design', label: 'System Design' }, { id: 'financial_analysis', label: 'Financials' },
    { id: 'savings_projection', label: 'Savings' }, { id: 'environmental_impact', label: 'Environmental' },
    { id: 'testimonials', label: 'Testimonials' }, { id: 'warranty', label: 'Warranty' },
    { id: 'terms', label: 'Terms' }, { id: 'signature', label: 'Signature' },
  ];

  useEffect(() => {
    api.get('/api/templates').then(res => {
      setBuiltIn(res.data.built_in || []);
      setCustom(res.data.custom || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleSection = (id) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.includes(id) ? prev.sections.filter(s => s !== id) : [...prev.sections, id]
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/templates', form);
      setCustom(prev => [res.data, ...prev]);
      setShowCreate(false);
      toast.success('Template created!');
    } catch { toast.error('Failed to create template'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/templates/${id}`); setCustom(prev => prev.filter(t => t.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const all = [...builtIn.map(t => ({ ...t, isBuiltIn: true })), ...custom.map(t => ({ ...t, isBuiltIn: false }))];
  const filtered = filter === 'all' ? all : all.filter(t => t.category === filter);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Proposal Templates</h1>
          <p className="text-text-muted text-sm mt-1">Industry presets and custom templates for every use case</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs py-2.5" data-testid="create-template-btn"><i className="fa-solid fa-plus mr-1.5" />Create Template</button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'residential', 'commercial', 'specialty', 'custom'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${filter === f ? 'bg-primary text-white' : 'bg-surface border border-border text-text-muted hover:border-primary/20'}`} data-testid={`filter-${f}`}>{f}</button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((t, i) => (
          <div key={t.id} className="card-interactive overflow-hidden animate-slide-up group" style={{ animationDelay: `${i * 60}ms` }} data-testid={`template-${t.id}`}>
            <div className="h-3" style={{ background: TEMPLATE_COLORS[t.id] || t.branding?.primary_color || '#16A34A' }} />
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: (TEMPLATE_COLORS[t.id] || t.branding?.primary_color || '#16A34A') + '15' }}>
                    <i className={`fa-solid ${TEMPLATE_ICONS[t.category] || 'fa-file'}`} style={{ color: TEMPLATE_COLORS[t.id] || t.branding?.primary_color || '#16A34A' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{t.name}</p>
                    <p className="text-[10px] text-text-muted capitalize">{t.category} {t.isBuiltIn && <span className="text-primary">Built-in</span>}</p>
                  </div>
                </div>
                {t.popularity && <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-lg">{t.popularity}%</span>}
              </div>
              <p className="text-xs text-text-secondary mb-3">{t.description}</p>
              <div className="flex flex-wrap gap-1 mb-4">
                {(t.sections || []).slice(0, 5).map(s => (
                  <span key={s} className="text-[9px] px-1.5 py-0.5 rounded bg-surface-alt text-text-muted capitalize">{s.replace(/_/g, ' ')}</span>
                ))}
                {(t.sections || []).length > 5 && <span className="text-[9px] text-text-muted">+{t.sections.length - 5}</span>}
              </div>
              <div className="flex gap-2">
                <button className="flex-1 btn-secondary text-xs py-2" data-testid={`use-template-${t.id}`}><i className="fa-solid fa-wand-magic-sparkles mr-1" />Use Template</button>
                {!t.isBuiltIn && <button onClick={() => handleDelete(t.id)} className="p-2 rounded-xl text-text-muted hover:text-destructive hover:bg-destructive-light transition-all"><i className="fa-solid fa-trash text-xs" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="create-template-modal">
            <button onClick={() => setShowCreate(false)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-5">Create Custom Template</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Template Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field h-9 text-sm" placeholder="My Template" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Description</label><input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input-field h-9 text-sm" placeholder="Template description..." /></div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Sections</label>
                <div className="grid grid-cols-2 gap-2">
                  {allSections.map(s => (
                    <button key={s.id} type="button" onClick={() => toggleSection(s.id)} className={`p-2 rounded-lg border text-left text-xs font-medium transition-all ${form.sections.includes(s.id) ? 'border-primary bg-primary/5 text-primary' : 'border-border text-text-muted hover:border-primary/20'}`}>
                      <i className={`fa-solid ${form.sections.includes(s.id) ? 'fa-check-square' : 'fa-square'} mr-1.5`} />{s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-text-muted">Primary Color</label>
                <input type="color" value={form.branding.primary_color} onChange={e => setForm({...form, branding: {...form.branding, primary_color: e.target.value}})} className="w-8 h-8 rounded cursor-pointer border-0" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="save-template-btn"><i className="fa-solid fa-save mr-1.5" />Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
